import { connect } from 'react-redux'
import type { CSSProperties } from 'react'
import { IGlobalState } from '../../Root/store/appReducer'
import { CompanyId, GoalWithProgress, Role } from '../../services/api.types'
import { getUserDto, hasRoleInCompany } from '../../common/auth/authSelectors'
import { getIsManualEntryInProgress } from '../../views/Progress/store/progressSelectors'
import YumManualEntryButtonAndDetails, { YumManualEntryButtonAndDetailsProps } from './YumManualEntryButtonAndDetails'

/**
 * Props that must be passed by the parent component.
 * userDto, hasRoleInCompany, and isManualEntryInProgress are provided by the wrapper.
 */
export interface YumManualEntryButtonAndDetailsWrapperOwnProps {
  /** The goal object - required */
  goal: GoalWithProgress
  /** Optional style to apply to the rendered container */
  style?: CSSProperties
  /** Whether token is present (leaderboard view) - optional */
  token?: string
  /** Whether this is the "me" view - optional, defaults to false */
  isMeView?: boolean
  /** Whether this is legacy ranking - optional, defaults to false */
  isLegacyRanking?: boolean
  /** Current showDetails state (controlled) - optional */
  showDetails?: boolean
  /** Callback when showDetails state changes - optional */
  onShowDetailsChange?: (showDetails: boolean) => void
  /** Compact mode - hides the "Show details" / "Hide details" link - optional, defaults to false */
  compact?: boolean
}

/**
 * Props that are provided by Redux state.
 * These are automatically attached by the wrapper - no need to pass them manually.
 */
interface StateProps {
  userDto: YumManualEntryButtonAndDetailsProps['userDto']
  hasRoleInCompany: YumManualEntryButtonAndDetailsProps['hasRoleInCompany']
  isManualEntryInProgress: YumManualEntryButtonAndDetailsProps['isManualEntryInProgress']
}

/**
 * Maps Redux state to component props.
 * Automatically provides userDto, hasRoleInCompany, and isManualEntryInProgress.
 */
const mapStateToProps = (state: IGlobalState, ownProps: YumManualEntryButtonAndDetailsWrapperOwnProps): StateProps => ({
  userDto: getUserDto(state),
  hasRoleInCompany: (companyId: CompanyId, role: Role) => hasRoleInCompany(state, companyId, role),
  isManualEntryInProgress: getIsManualEntryInProgress(state),
})

/**
 * Wrapper component for YumManualEntryButtonAndDetails.
 * Automatically provides userDto, hasRoleInCompany, and isManualEntryInProgress from Redux store.
 *
 * Usage:
 * <YumManualEntryButtonAndDetailsWrapper
 *   goal={goal}
 *   token={token}
 *   isMeView={false}
 *   compact={true}
 * />
 *
 * Instead of:
 * <YumManualEntryButtonAndDetails
 *   goal={goal}
 *   token={token}
 *   userDto={userDto}                    // automatically provided
 *   hasRoleInCompany={hasRoleInCompany}  // automatically provided
 *   isManualEntryInProgress={false}      // automatically provided
 *   isMeView={false}
 *   compact={true}
 * />
 */
export default connect(mapStateToProps)(YumManualEntryButtonAndDetails)
